import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {v4 as uuidV4} from 'uuid';
import classNames from 'classnames';
import {getWindow} from 'ssr-window';
import FormElementError from '@spothero/ui/form-element-error';
import Label from '@spothero/ui/label';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';

const window = getWindow();

export default class PromocodeControl extends Component {
    static propTypes = {
        className: PropTypes.string,
        promoCode: PropTypes.string,
        active: PropTypes.bool,
        onChange: PropTypes.func,
        verifyInline: PropTypes.bool,
        referralCode: PropTypes.bool,
        errors: PropTypes.arrayOf(PropTypes.string),
        /** Placeholder text to put in the promo code input. If one is provided then no label is applied. */
        placeholder: PropTypes.string,
        spotId: PropTypes.number,
        rate: PropTypes.object,
        userEmail: PropTypes.string,
        showCancel: PropTypes.bool,
        autoFocus: PropTypes.bool,
        enterSubmits: PropTypes.bool,
        onCancelPromoCode: PropTypes.func,
        onSubmitPromo: PropTypes.func,
    };
    static defaultProps = {
        promoCode: '',
        placeholder: '',
        showCancel: true,
        autoFocus: true,
    };

    constructor(props) {
        super(props);

        const {promoCode, active, errors} = props;

        this.state = {
            promoCode,
            active,
            errors: errors || [],
            isLoading: false,
            key: uuidV4(),
        };
    }

    componentDidMount() {
        const {promoCode} = this.state;

        if (!isEmpty(promoCode)) {
            this.submit();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {onChange, onCancelPromoCode} = this.props;
        const {active, promoCode} = this.state;

        if (onChange) {
            onChange(promoCode);
        }

        if (prevState.active !== active && !active && onCancelPromoCode) {
            onCancelPromoCode();
        }
    }

    componentWillUnmount() {
        this._removeListeners();
    }

    _onToggleActiveClick = () => {
        this.setState((prevState, props) => {
            return {
                active: !prevState.active,
                isLoading: false,
                errors: [],
                promoCode: '',
                key: uuidV4(),
            };
        });
    };

    _onInputFocus = evt => {
        window.addEventListener('keydown', this._onKeyDown);
    };

    _onInputBlur = evt => {
        this._removeListeners();
    };

    _onInputChange = evt => {
        this.setState({
            promoCode: evt.target.value,
            errors: [],
        });
    };

    _onInvalid = validationState => {
        const {referralCode, showCancel} = this.props;
        const codeType = referralCode ? 'referral' : 'promo';

        this.setState({
            errors: [
                `Please enter a ${codeType} code${
                    showCancel ? ' or click Cancel' : ''
                }.`,
            ],
        });
    };

    _onKeyDown = evt => {
        if (this.props.enterSubmits && evt.which === 13) {
            this._onApplyClick();
        }
    };

    _onApplyClick = () => {
        this._checkCode();
    };

    _removeListeners = () => {
        window.removeEventListener('keydown', this._onKeyDown);
    };

    _checkCode = evt => {
        const {spotId, rate, userEmail, onSubmitPromo} = this.props;
        const {promoCode} = this.state;

        if (!userEmail) {
            this.setState({
                errors: [
                    'You must first be logged in or provide an email address above.',
                ],
            });
        } else if (!isEmpty(promoCode)) {
            this.setState({
                isLoading: true,
            });

            onSubmitPromo({
                email: userEmail,
                price: rate.price,
                promoCode,
                ruleGroupId: rate.rule_group_id,
                ruleType: rate.rule_type,
                spotId,
                currencyType: rate.currency_type,
            }).catch(error => {
                this.setState({
                    errors: [error.message],
                    isLoading: false,
                });
            });
        }
    };

    submit() {
        const {active, promoCode} = this.state;

        if (active) {
            return {promoCode};
        } else {
            return true;
        }
    }

    render() {
        const {
            className,
            verifyInline,
            referralCode,
            placeholder,
            showCancel,
            autoFocus,
        } = this.props;
        const {promoCode, active, isLoading, key, errors} = this.state;
        const classes = classNames(
            'PromocodeControl',
            'FormElement',
            {ContainsErrors: Boolean(errors.length)},
            className
        );
        const label = isEmpty(placeholder)
            ? `${referralCode ? 'Referral' : 'Promo'} Code`
            : null;
        const isApplyDisabled = isEmpty(promoCode) || isLoading;
        const codeType = referralCode ? 'referral' : 'promo';

        return (
            <div className={classes}>
                {active ? (
                    <>
                        <TextInput
                            key={key}
                            className={
                                isEmpty(promoCode)
                                    ? ''
                                    : 'PromocodeControl-text-entered'
                            }
                            name="promoCode"
                            label={<Label>{label}</Label>}
                            placeholder={placeholder}
                            defaultValue={promoCode}
                            autoFocus={autoFocus}
                            onChange={this._onInputChange}
                            onFocus={this._onInputFocus}
                            onBlur={this._onInputBlur}
                            onInvalid={this._onInvalid}
                            required
                        />
                        <div className="PromocodeControl-controls">
                            {verifyInline && (
                                <TextButton
                                    data-test="apply-promocode"
                                    disabled={isApplyDisabled}
                                    loading={isLoading}
                                    onClick={this._onApplyClick}
                                >
                                    Apply
                                </TextButton>
                            )}
                            {showCancel && (
                                <TextButton onClick={this._onToggleActiveClick}>
                                    Cancel
                                </TextButton>
                            )}
                        </div>
                    </>
                ) : (
                    <TextButton
                        data-test="have-promocode"
                        onClick={this._onToggleActiveClick}
                    >
                        Add {codeType} code
                    </TextButton>
                )}
                {Boolean(errors.length) &&
                    errors.map((e, i) => {
                        return <FormElementError key={i}>{e}</FormElementError>;
                    })}
            </div>
        );
    }
}
